/**
 * GDSStepOrchestrator.js
 * Orchestrates the rendering of GDS Builder steps.
 */
import { GDSDropdownsStepRenderer } from './GDSDropdownsStepRenderer.js';
import { GDSEnvironmentStepRenderer } from './GDSEnvironmentStepRenderer.js';
import { GDSCardSelectionStepRenderer } from './GDSCardSelectionStepRenderer.js';

export class GDSStepOrchestrator {
    constructor(builder) {
        if (!builder) {
            throw new Error('[GDSStepOrchestrator] Constructor requires a builder instance.');
        }
        this.builder = builder;
        this.state = builder.stateManager;

        // Initialize specific step renderers, passing the orchestrator instance
        this.dropdownsRenderer = new GDSDropdownsStepRenderer(this);
        this.environmentRenderer = new GDSEnvironmentStepRenderer(this);
        this.cardSelectionRenderer = new GDSCardSelectionStepRenderer(this);

        // Map page types to their corresponding renderer instances
        this.rendererMap = {
            'dropdowns': this.dropdownsRenderer,
            'environment': this.environmentRenderer,
            'card_selection': this.cardSelectionRenderer
        };

        // Map step numbers to their corresponding renderer instances
        this.stepRenderers = {
            1: this.dropdownsRenderer,
            2: this.environmentRenderer,
            3: this.cardSelectionRenderer
        };
    }

    get contentContainer() {
        return this.builder?.contentContainer || null;
    }

    async renderCurrentStep() {
        const currentStep = this.builder.stateManager.currentStep;

        // Clear current content
        if (this.builder.contentContainer) {
            this.builder.contentContainer.innerHTML = '';
        } else {
            console.error('[GDSStepOrchestrator] No content container found!');
            return;
        }

        // Get the renderer for the current step
        const renderer = this.stepRenderers[currentStep];

        if (!renderer) {
            console.error(`[GDSStepOrchestrator] No renderer found for step ${currentStep}`);
            this.builder.uiFeedback.showError(`Invalid step: ${currentStep}`);
            return;
        }

        try {
            await renderer.render();

            // Update progress indicators
            this.updateProgressIndicators();

            // Update navigation button states
            this.updateNavigationButtons();

            if (window.innerWidth < 768) {
                window.scrollTo({ top: 0, behavior: 'smooth' });
                console.log('Scrolling to top');
            }

        } catch (error) {
            console.error('[GDSStepOrchestrator] Error rendering step:', error);
            console.error('[GDSStepOrchestrator] Error stack:', error.stack);
            this.builder.uiFeedback.showError('Error loading step. Please refresh the page.');
        }
    }

    /**
     * Updates progress indicators (if any exist)
     */
    updateProgressIndicators() {
        // Check if progressManager exists before using it
        if (this.builder.progressManager && typeof this.builder.progressManager.updateProgress === 'function') {
            this.builder.progressManager.updateProgress(this.state.currentStep);
        }
    }

    /**
     * Updates navigation button states
     */
    updateNavigationButtons() {
        this.updateButtonVisibility();
    }

    collectFormData() {
        if (!this.state?.context) {
            console.error('[Orchestrator] Cannot collect form data: state or context is missing');
            return null;
        }

        const pageType = this.state.context.page_type;
        const renderer = this.rendererMap[pageType];

        if (renderer && typeof renderer.collectFormData === 'function') {
            return renderer.collectFormData();
        }

        console.error(`[Orchestrator] No form data collection method for page type: ${pageType}`);
        return null;
    }

    updateButtonVisibility() {
        try {
            this.updateNextButtonVisibility();
            this.updateBackButtonVisibility();
        } catch (e) {
            console.error('[Orchestrator] Error updating button visibility:', e);
        }
    }

    updateNextButtonVisibility() {
        const btn = this.builder?.submitButton;
        if (!btn) return;

        const pageType = this.state?.context?.page_type;
        const renderer = this.rendererMap[pageType];

        if (renderer && typeof renderer.getButtonConfig === 'function') {
            const buttonConfig = renderer.getButtonConfig();
            btn.innerHTML = buttonConfig.innerHTML;
            btn.classList.toggle('hidden', !buttonConfig.isVisible);
            btn.disabled = !buttonConfig.isEnabled;
        } else {
            btn.classList.add('hidden');
        }
    }

    /**
     * Updates the visibility of the "Back" button.
     * The button should only be visible if the current step is not the first step.
     */
    updateBackButtonVisibility() {
        if (this.builder.backButton) {
            // --- FIX: Base visibility on the current step number ---
            const isFirstStep = this.state.currentStep === 1;
            this.builder.backButton.style.visibility = isFirstStep ? 'hidden' : 'visible';
        }
    }

    /**
     * Gets a reference to the main builder instance.
     * @returns {GDSBuilder}
     */
    getBuilder() {
        return this.builder;
    }
}
